/**
 * Keep Site About's camera status fresh until cameras report (ENG-2853).
 *
 * Status reaches admin within ~10 s of a connector's first frame, but the page
 * fetched `camera/{id}/status/` once per load, so anyone who opened it before
 * the first frame kept seeing the poster and "No data yet" until a reload
 * (EyeQ: "up to ~10 min after onboarding or arming").
 *
 * Two modes, both capped at ten minutes:
 * - pending: on load, re-fetch only the cameras with no status yet, every
 *   15 s, and stop once they have all reported;
 * - all: after an Arm / Reboot from this page, re-fetch every camera every
 *   30 s, since a restart replaces previews and connection results that were
 *   already there.
 */

import React from "react";
import { createApi } from "../../../../utils/createApi";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";

export const PENDING_POLL_MS = 15000;
export const AFTER_ACTION_POLL_MS = 30000;
export const POLL_WINDOW_MS = 10 * 60 * 1000;

/** Window event the Arm / Reboot handlers fire to start the "all" mode. */
export const CAMERA_STATUS_REFRESH_EVENT = "siteAboutCameraStatusRefresh";

export const requestCameraStatusRefresh = () => {
  window.dispatchEvent(new CustomEvent(CAMERA_STATUS_REFRESH_EVENT));
};

/** No status yet: never loaded, or loaded before the connector's first frame. */
export const isCameraStatusPending = (status: any): boolean =>
  !status ||
  status.connected === null ||
  status.connected === undefined ||
  status.frame_since_launch === null ||
  status.frame_since_launch === undefined ||
  !String(status.preview_url ?? "").trim();

export const camerasToPoll = (
  mode: "pending" | "all",
  cameraIds: number[],
  statusById: Record<number, unknown>
): number[] =>
  mode === "all"
    ? cameraIds
    : cameraIds.filter((id) => isCameraStatusPending(statusById[id]));

/** Re-fetch status for these cameras; a failed camera keeps its old status. */
export const refreshCameraStatuses = async (cameraIds: number[]) => {
  const AxiosUI = createApi("");
  await Promise.allSettled(
    cameraIds.map(async (id) => {
      const response = await AxiosUI.get(`/camera/${id}/status/`);
      const store = useSiteAboutStore.getState();
      store.setCameraStatusForCamera(id, response.data);
      const prefetch = store.rowPrefetchStateByCamera[id];
      if (prefetch && !prefetch.statusLoaded) {
        store.setRowPrefetchStateForCamera(id, {
          ...prefetch,
          statusLoaded: true,
          statusFailed: false,
        });
      }
    })
  );
};

export const useCameraStatusPolling = (cameraIds: number[]) => {
  const idsKey = cameraIds.join(",");
  const idsRef = React.useRef(cameraIds);
  idsRef.current = cameraIds;
  const modeRef = React.useRef<"pending" | "all">("pending");
  const deadlineRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const schedule = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const delay =
      modeRef.current === "all" ? AFTER_ACTION_POLL_MS : PENDING_POLL_MS;
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      if (Date.now() > deadlineRef.current) return;
      const ids = camerasToPoll(
        modeRef.current,
        idsRef.current,
        useSiteAboutStore.getState().cameraStatusById
      );
      if (ids.length === 0) return;
      if (!document.hidden) await refreshCameraStatuses(ids);
      schedule();
    }, delay);
  }, []);

  const start = React.useCallback(
    (mode: "pending" | "all") => {
      // A page-load restart must not cut short an after-action window.
      if (mode === "pending" && modeRef.current === "all" && timerRef.current) {
        return;
      }
      modeRef.current = mode;
      deadlineRef.current = Date.now() + POLL_WINDOW_MS;
      schedule();
    },
    [schedule]
  );

  React.useEffect(() => {
    if (idsKey) start("pending");
  }, [idsKey, start]);

  React.useEffect(() => {
    const onRefresh = () => start("all");
    window.addEventListener(CAMERA_STATUS_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(CAMERA_STATUS_REFRESH_EVENT, onRefresh);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [start]);
};
